import PropTypes from 'prop-types';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { approveStockReturn } from '#/api/imsMgmt/receipts/stockReturn/approve';
import { cancelStockReturn } from '#/api/imsMgmt/receipts/stockReturn/cancel';
import { fetchStockReturnDetail } from '#/api/imsMgmt/receipts/stockReturn/fetchDetail';
import { rejectStockReturn } from '#/api/imsMgmt/receipts/stockReturn/reject';
import { submitStockReturn } from '#/api/imsMgmt/receipts/stockReturn/submit';
import SideMsg from '#/modules/commonComponents/SideMsg';
import DocumentSideMsgActions from '#/modules/imsMgmt/receipts/shared/DocumentSideMsgActions';
import { useDocumentSideMsgWorkflow } from '#/modules/imsMgmt/receipts/shared/useDocumentSideMsgWorkflow.jsx';
import {
  stockReturnSideMsgDetailBlock,
  stockReturnSideMsgStatusBlock,
} from '#/modules/imsMgmt/receipts/stockReturn/data/stockReturnSideMsgConfig';
import {
  getStockReturnApiErrorMessage,
  isStockReturnVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/apiError';
import {
  canApproveStockReturn,
  canCancelStockReturn,
  canRejectStockReturn,
  canSubmitStockReturn,
} from '#/modules/imsMgmt/receipts/stockReturn/helper/returnStatus';
import {
  stockReturnDetailQueryKey,
  stockReturnListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CONFIRM_COPY = {
  submit: {
    title: '提交退料單',
    message: '提交後將鎖定來源庫存並進入待核准，確定提交？',
    confirmText: '提交',
  },
  approve: {
    title: '核准退料單',
    message: '核准後將移動庫存至目的倉，確定核准？',
    confirmText: '核准',
  },
  reject: {
    title: '退回退料單',
    message: '退回後將恢復為草稿並釋放鎖定，確定退回？',
    confirmText: '退回',
  },
  cancel: {
    title: '刪除草稿',
    message: '刪除後無法復原，確定刪除？',
    confirmText: '刪除',
  },
};

const APIS = {
  submit: submitStockReturn,
  approve: approveStockReturn,
  reject: rejectStockReturn,
  cancel: cancelStockReturn,
  fetchDetail: fetchStockReturnDetail,
};

const StockReturnSideMsg = ({ displayData, setSelectedData }) => {
  const canCreate = useCan(PERM.STOCK_RETURN_CREATE);
  const canReview = useCan(PERM.STOCK_RETURN_REVIEW);

  const workflow = useDocumentSideMsgWorkflow({
    row: displayData,
    setSelectedData,
    confirmCopy: CONFIRM_COPY,
    canSubmit: (status) => canCreate && canSubmitStockReturn(status),
    canApprove: (status) => canReview && canApproveStockReturn(status),
    canReject: (status) => canReview && canRejectStockReturn(status),
    canCancel: (status) => canCreate && canCancelStockReturn(status),
    apis: APIS,
    listQueryKeyPrefix: stockReturnListQueryKeyPrefix,
    detailQueryKey: stockReturnDetailQueryKey,
    getErrorMessage: getStockReturnApiErrorMessage,
    isVersionConflict: isStockReturnVersionConflict,
  });

  const hasActions = (
    workflow.canSubmit
    || workflow.canApprove
    || workflow.canReject
    || workflow.canCancel
  );

  const blocks = [
    stockReturnSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <DocumentSideMsgActions
                canSubmit={workflow.canSubmit}
                canApprove={workflow.canApprove}
                canReject={workflow.canReject}
                canCancel={workflow.canCancel}
                isPending={workflow.isPending}
                onSubmit={workflow.handleSubmit}
                onApprove={workflow.handleApprove}
                onReject={workflow.handleReject}
                onCancel={workflow.handleCancel}
              />
            </div>
          ),
        },
      ],
    },
    stockReturnSideMsgDetailBlock,
  ];

  return (
    <>
      {workflow.ConfirmDialogHost}
      <workflow.RejectDialogHost />
      <SideMsg
        title="退料單"
        titleValue="returnNo"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

StockReturnSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default StockReturnSideMsg;
